import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { getEventAgencyLinks, getEventRegistrations } from '@/lib/api';
import {
  CONSENT_VALUES,
  consentLabel,
  displayValue,
  formatDate,
} from '@/lib/eventStudio';
import { SIGNUP_STATUSES, signupStatusMeta } from '@/lib/websiteEngagement';
import { Button } from '@/components/ui/button';
import PortalEmailComposer from '@/components/admin/PortalEmailComposer';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  ROW_DENSITY_CLASSES,
  StatusPill,
  TableEmptyRow,
  TableFilterCard,
  adminInputClass,
  filterPillActiveClass,
  filterPillClass,
  tableCardClass,
  tableCellClass,
  tableHeadCellClass,
  tableHeaderRowClass,
  tableRowClass,
} from '@/components/admin/AdminTableStyles';
import { cn } from '@/lib/utils';
import { ArrowLeft, Camera, Search, Users } from 'lucide-react';

const ALL = 'all';
const ROW_PADDING = ROW_DENSITY_CLASSES.comfortable;

const COLUMNS = [
  { id: 'name', label: 'Name', min: 160, grow: 1.3 },
  { id: 'registration', label: 'Registration ID', min: 130, grow: 0.9 },
  { id: 'email', label: 'Email', min: 190, grow: 1.5 },
  // Whether this person is coming at all, and whether they hold a seat. A
  // cancelled attendee used to be indistinguishable from a confirmed one here.
  { id: 'status', label: 'Status', min: 115, grow: 0.8 },
  { id: 'attendance', label: 'Attendance', min: 105, grow: 0.7 },
  { id: 'photo', label: 'Photography', min: 115, grow: 0.8 },
  { id: 'marketing', label: 'Marketing', min: 115, grow: 0.8 },
  { id: 'referral', label: 'Referred by', min: 140, grow: 1 },
  { id: 'locale', label: 'Language', min: 95, grow: 0.6 },
  { id: 'registered', label: 'Registered', min: 110, grow: 0.7 },
];
const GRID = COLUMNS.map((column) => `minmax(${column.min}px, ${column.grow}fr)`).join(' ');
const TABLE_MIN_WIDTH = COLUMNS.reduce((total, column) => total + column.min, 0);

export default function AdminEventRegistrations() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mediaFilter, setMediaFilter] = useState(ALL);
  const [marketingFilter, setMarketingFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  // Agencies that have a share link on this event, for the "referred by"
  // filter. An event with no agency links never shows the control at all.
  const [agencies, setAgencies] = useState([]);
  const [agencyFilter, setAgencyFilter] = useState(ALL);

  useEffect(() => {
    const timer = window.setTimeout(() => setQ(searchInput.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (mediaFilter !== ALL) params.media_choice = mediaFilter;
      if (marketingFilter !== ALL) params.marketing_choice = marketingFilter;
      if (statusFilter !== ALL) params.status = statusFilter;
      if (agencyFilter !== ALL) params.agent_id = agencyFilter;
      if (q) params.q = q;
      const response = await getEventRegistrations(id, params);
      setData(response.data);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not load registrations');
    } finally {
      setLoading(false);
    }
  }, [id, mediaFilter, marketingFilter, statusFilter, agencyFilter, q]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getEventAgencyLinks(id)
      .then(({ data: payload }) => setAgencies(payload?.leaderboard || []))
      .catch(console.error);
  }, [id]);

  const items = data?.items || [];
  const hasFilters = mediaFilter !== ALL || marketingFilter !== ALL
    || statusFilter !== ALL || agencyFilter !== ALL || !!q;
  // Two different numbers, and the header used to print the wrong one as the
  // headline: the event's seat count is what "registered" means, while the row
  // count is however many the current filters left behind — cancelled and
  // waitlisted people included.
  const seats = data?.event?.registration_count ?? 0;
  const waiting = data?.event?.waitlist_count ?? 0;
  const rows = data?.total ?? 0;
  const filtered = hasFilters || rows !== seats;

  return (
    <div className="animate-fade-in space-y-5">
      <div className="space-y-2">
        <Button variant="outline" size="sm" onClick={() => navigate(`/admin/events/${id}`)}
          className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to event
        </Button>
        <h2 className="flex items-center gap-2 text-2xl font-semibold text-[#062B1A]"
          style={{ fontFamily: 'var(--font-display)' }}>
          <Users className="h-6 w-6 text-[#D4AF37]" />
          {data?.event?.title || 'Attendees'}
        </h2>
        <div className="flex flex-wrap items-center gap-2 text-sm text-[#062B1A]/60">
          <span>{seats} registered</span>
          {!!data?.event?.capacity && <span>· capacity {data.event.capacity}</span>}
          {!!waiting && <span>· {waiting} waitlisted</span>}
          {filtered && <span>· showing {rows}</span>}
          {/* The number staff need on the day: who must not be photographed. */}
          {!!data?.no_photo_total && (
            <StatusPill tone="red">
              <Camera className="mr-1 inline size-3" />
              {data.no_photo_total} no-photo
            </StatusPill>
          )}
          {!!data?.marketing_opt_in_total && (
            <StatusPill tone="emerald">{data.marketing_opt_in_total} marketing opt-in</StatusPill>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <PortalEmailComposer
          audience="event_registrations"
          recipients={items}
          buttonLabel={items.length ? `Email filtered attendees (${items.length})` : 'Email attendees'}
          disabled={items.length === 0}
          buttonClassName="border-[#D4AF37]/45 text-[#7A5A05]"
        />
      </div>

      <TableFilterCard>
        <div className="relative max-w-xl flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#062B1A]/40" />
          <Input
            type="search"
            aria-label="Search attendees"
            placeholder="Search name, email or registration ID..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            className={cn('pl-9', adminInputClass)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-[#062B1A]/45">Status</span>
          {[{ value: ALL, label: 'All' },
            ...SIGNUP_STATUSES.map((value) => ({ value, label: signupStatusMeta(value).label }))
          ].map((option) => (
            <Button
              key={`status-${option.value}`} type="button" variant="outline" size="sm"
              aria-pressed={statusFilter === option.value}
              onClick={() => setStatusFilter(option.value)}
              className={cn(filterPillClass, statusFilter === option.value && filterPillActiveClass)}
            >
              {option.label}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-[#062B1A]/45">Photography</span>
          {[{ value: ALL, label: 'All' }, ...CONSENT_VALUES].map((option) => (
            <Button
              key={`media-${option.value}`} type="button" variant="outline" size="sm"
              aria-pressed={mediaFilter === option.value}
              onClick={() => setMediaFilter(option.value)}
              className={cn(filterPillClass, mediaFilter === option.value && filterPillActiveClass)}
            >
              {option.label}
            </Button>
          ))}
          <span className="ml-2 text-xs font-semibold uppercase tracking-wide text-[#062B1A]/45">Marketing</span>
          {[{ value: ALL, label: 'All' }, ...CONSENT_VALUES].map((option) => (
            <Button
              key={`marketing-${option.value}`} type="button" variant="outline" size="sm"
              aria-pressed={marketingFilter === option.value}
              onClick={() => setMarketingFilter(option.value)}
              className={cn(filterPillClass, marketingFilter === option.value && filterPillActiveClass)}
            >
              {option.label}
            </Button>
          ))}
        </div>
        {agencies.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-[#062B1A]/45">Agency</span>
            {[{ agent_id: ALL, agency_name: 'All' }, ...agencies].map((option) => (
              <Button
                key={`agency-${option.agent_id}`} type="button" variant="outline" size="sm"
                aria-pressed={agencyFilter === option.agent_id}
                onClick={() => setAgencyFilter(option.agent_id)}
                className={cn(filterPillClass, agencyFilter === option.agent_id && filterPillActiveClass)}
              >
                {option.agency_name}
              </Button>
            ))}
          </div>
        )}
      </TableFilterCard>

      <Card className={tableCardClass}>
        <Table style={{ minWidth: `${TABLE_MIN_WIDTH}px` }}>
          <TableHeader>
            <TableRow className={cn('grid', tableHeaderRowClass)} style={{ gridTemplateColumns: GRID }}>
              {COLUMNS.map((column) => (
                <TableHead key={column.id} scope="col" className={cn(tableHeadCellClass, 'flex items-center')}>
                  {column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index} className={cn('grid', tableRowClass)} style={{ gridTemplateColumns: GRID }}>
                  {COLUMNS.map((column) => (
                    <TableCell key={column.id} className={cn(tableCellClass, ROW_PADDING)}>
                      <div className="h-4 animate-pulse rounded bg-[#F1F3F5]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableEmptyRow colSpan={COLUMNS.length}>
                {hasFilters ? 'No attendees match these filters.' : 'Nobody has registered yet.'}
              </TableEmptyRow>
            ) : (
              items.map((person) => {
                const cell = cn(tableCellClass, ROW_PADDING);
                const media = person.consent?.media_choice;
                const marketing = person.consent?.marketing_choice;
                return (
                  <TableRow
                    key={person.id}
                    data-testid="attendee-row"
                    onClick={() => navigate(`/admin/website-signups/${person.id}`)}
                    className={cn('grid cursor-pointer', tableRowClass)}
                    style={{ gridTemplateColumns: GRID }}
                  >
                    <TableCell className={cn(cell, 'font-bold text-[#062B1A]')}>
                      {displayValue(person.full_name)}
                      {person.consent?.is_minor && (
                        <span className="ml-1 text-[10px] font-normal text-[#8A6508]">(minor)</span>
                      )}
                    </TableCell>
                    <TableCell className={cn(cell, 'font-mono text-[11px] text-[#062B1A]/70')}>
                      {displayValue(person.registration_id)}
                    </TableCell>
                    <TableCell className={cn(cell, 'text-[#062B1A]/70')}>
                      <span className="line-clamp-2 break-all">{displayValue(person.email)}</span>
                    </TableCell>
                    <TableCell className={cell}>
                      <StatusPill tone={signupStatusMeta(person.status).pillTone}>
                        {signupStatusMeta(person.status).label}
                      </StatusPill>
                      {person.waitlisted && person.status !== 'cancelled' && (
                        <span className="ml-1 text-[10px] text-[#8A6508]">waitlist</span>
                      )}
                    </TableCell>
                    <TableCell className={cn(cell, 'text-[#062B1A]/70')}>
                      {displayValue(person.attendance_preference)}
                    </TableCell>
                    <TableCell className={cell}>
                      <StatusPill tone={media === 'no_consent' ? 'red' : media === 'consent' ? 'emerald' : 'neutral'}>
                        {consentLabel(media)}
                      </StatusPill>
                    </TableCell>
                    <TableCell className={cell}>
                      <StatusPill tone={marketing === 'consent' ? 'emerald' : 'neutral'}>
                        {consentLabel(marketing)}
                      </StatusPill>
                    </TableCell>
                    <TableCell className={cn(cell, 'text-[#062B1A]/70')}>
                      {person.agency_link ? (
                        <>
                          <span className="block truncate">{person.agency_link.agency_name}</span>
                          <span className="block truncate text-[10px] text-[#062B1A]/45">
                            {[person.agency_link.channel_label, person.agency_link.label]
                              .filter(Boolean).join(' · ')}
                          </span>
                        </>
                      ) : '—'}
                    </TableCell>
                    <TableCell className={cn(cell, 'text-[#062B1A]/70')}>
                      {displayValue(person.locale)}
                    </TableCell>
                    <TableCell className={cn(cell, 'text-[#062B1A]/60')}>
                      {formatDate(person.created_at)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
